import React from "react";
import { useTranslation } from "react-i18next";
import AppTable from "../../../../shared/AppTable";
import ValidationErrors from "../../../../components/ValidationErrors";

const normalizeCat = (cat = "") =>
  String(cat).toLowerCase().replace(/[\s_-]+/g, "");

const VITAL_KEYS = [
  "vitalsignheight",
  "vitalsignweight",
  "vitalsignsystolic",
  "vitalsigndiastolic",
  "temperature",
  "pulse",
  "respiratoryrate",
  "oxygensaturation",
];

const VITAL_ORDER = {
  vitalsignsystolic: 1,
  vitalsigndiastolic: 2,
  vitalsignheight: 3,
  vitalsignweight: 4,
  temperature: 5,
  pulse: 6,
  respiratoryrate: 7,
  oxygensaturation: 8,
};

const isVital = (cat) => VITAL_KEYS.includes(normalizeCat(cat));

const UNIT_DISPLAY = {
  "mm[Hg]": "mmHg",
  mmhg: "mmHg",
  Cel: "C",
  cel: "C",
  degC: "C",
  kg: "kg",
  cm: "cm",
  "%": "%",
  "/min": "/min",
  bpm: "bpm",
  h: "h",
  hr: "h",
};

const formatUnit = (row) => {
  const unit = row?.unit_display || row?.unit;
  if (!unit) return "";
  return UNIT_DISPLAY[unit] || unit;
};

const REASON_DISPLAY = {
  vitalsignheight: { "999.9": "Not Measured" },
  vitalsignweight: { "999.9": "Not Collected" },
  vitalsignsystolic: { "888": "Not measurable", "999": "Unknown" },
  vitalsigndiastolic: { "888": "Not measurable", "999": "Unknown" },
  temperature: { "88": "Not Obtained", "99": "Unknown" },
  pulse: {
    "997": "Cardiac Arrest",
    "998": "Not Recorded",
    "999": "Not Stated",
  },
  respiratoryrate: {
    "00": "Artificial Ventilation",
    "88": "Not Obtained",
    "99": "Unknown",
  },
  oxygensaturation: { "888": "Not Obtained", "999": "Unknown" },
};

const formatReason = (row) => {
  const reason = row?.reason;
  if (!reason) return "";
  const catKey = normalizeCat(row?.category);
  const mapped = REASON_DISPLAY[catKey]?.[reason];
  return mapped || reason;
};

const SupportingInfoTable = ({ data, loading }) => {
  const { t } = useTranslation();
  const hasValidation = (row) =>
    row?.has_validation_error === true ||
    (Array.isArray(row?.validation_errors) && row.validation_errors.length > 0);

  function downloadBase64File(base64Data, fileName, fileType) {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: fileType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  const allRows = data?.data?.supporting_info || [];

  const vitals = allRows
    .filter((row) => isVital(row?.category))
    .sort((a, b) => {
      const oa = VITAL_ORDER[normalizeCat(a?.category)] ?? 99;
      const ob = VITAL_ORDER[normalizeCat(b?.category)] ?? 99;
      return oa - ob;
    });

  const others = allRows.filter((row) => !isVital(row?.category));

  const valueRenderer = (row) => {
    if (row?.image_title || row?.image_value || row?.image_url) {
      return (
        <button
          style={{ backgroundColor: "#0070C2" }}
          className="px-2 py-0.5 rounded text-[0.65rem] text-white hover:bg-[#005a9c] transition-colors"
          onClick={() => {
            if (row?.image_url && row?.image_url !== "") {
              const a = document.createElement("a");
              a.href = row.image_url;
              a.download = row?.image_title || "download";
              a.target = "_blank";
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            } else if (row?.image_value) {
              downloadBase64File(
                row?.image_value,
                row?.image_title,
                row?.image_content_type,
              );
            }
          }}
        >
          {row?.image_title}
        </button>
      );
    }

    const rawValue = row?.value ?? row?.value_string;
    const hasValue =
      rawValue !== null &&
      rawValue !== undefined &&
      String(rawValue).trim() !== "";

    if (hasValue) {
      const unit = formatUnit(row);
      return (
        <span className="whitespace-pre-wrap text-xs">
          {rawValue}
          {unit ? <span className="text-slate-500"> {unit}</span> : null}
        </span>
      );
    }

    const reason = formatReason(row);
    if (reason) {
      return (
        <span className="whitespace-pre-wrap text-xs text-slate-500 italic capitalize">
          {reason}
        </span>
      );
    }

    return <span className="whitespace-pre-wrap text-xs">{t("N/A")}</span>;
  };

  const columns = [
    {
      header: "#",
      render: (row, index) => index + 1,
      width: 50,
      align: "center",
    },
    {
      header: t("Category"),
      render: (row) => row?.category || t("N/A"),
      width: 240,
    },
    {
      header: t("Code"),
      render: (row) => (
        <span className="font-semibold text-[#0070C2] text-xs">
          {row?.code || t("N/A")}
        </span>
      ),
      width: 160,
    },
    {
      header: t("Value"),
      render: (row) => valueRenderer(row),
    },
  ];

  const renderTable = (rows) => (
    <AppTable
      columns={columns}
      data={rows}
      loading={loading}
      actions={false}
      renderRowBelow={(row) =>
        hasValidation(row) ? (
          <ValidationErrors validationErrors={row.validation_errors} compact />
        ) : null
      }
      getRowSx={(row) => ({
        backgroundColor: hasValidation(row) ? "#FEF3C7" : "transparent",
        "&:hover": {
          backgroundColor: hasValidation(row)
            ? "#FDE68A !important"
            : "rgba(0, 0, 0, 0.04)",
        },
      })}
    />
  );

  return (
    <>
      {vitals.length > 0 && (
        <div className="mb-3">
          <div className="text-[13px] font-bold text-slate-600 mb-1">
            {t("Vital Signs")}
          </div>
          {renderTable(vitals)}
        </div>
      )}

      {others.length > 0 && (
        <div>
          <div className="text-[13px] font-bold text-slate-600 mb-1">
            {t("Clinical & Others")}
          </div>
          {renderTable(others)}
        </div>
      )}

      {allRows.length === 0 && renderTable([])}
    </>
  );
};

export default SupportingInfoTable;
